import React, { useState, useEffect } from 'react';
import './VendorProfile.css';

function VendorProfile() {
  const [activeTab, setActiveTab] = useState('about');
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for vendor data
  const [vendorData, setVendorData] = useState({
    name: "",
    tagline: "",
    rating: 0,
    reviewCount: 0,
    verified: false,
    avatar: "/assets/genie.png",
    coverImage: "/assets/bg3.jpg",
    description: "",
    services: [],
    contact: {
      email: "",
      phone: "",
      website: "",
      address: ""
    },
    availability: {
      hours: "Monday - Sunday: 9:00 AM - 5:00 PM",
      responseTime: "Usually responds within 2 hours"
    },
    subscription: {
      type: "",
      status: "",
      startDate: "",
      endDate: "",
      totalAmount: 0
    }
  });

  // Form state for editing
  const [formData, setFormData] = useState({...vendorData});

  useEffect(() => {
    // Fetch profile data when component mounts
    fetchProfileData();
  }, []);

  useEffect(() => {
    // Reset form data when switching between view and edit modes
    setFormData({...vendorData});
  }, [isEditing, vendorData]);

  const fetchProfileData = async () => {
    setIsLoading(true);
    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await fetch('http://203.161.55.192:8086/vservice/api/v1/auth/profile', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch profile data');
      }

      const data = await response.json();
      
      // Transform API data to match our component structure
      const transformedData = {
        name: data.businessName || "Business Name",
        tagline: `${data.firstName} ${data.otherName}`,
        rating: 4.8, // Default rating since not provided in API
        reviewCount: 0, // Default since not provided in API
        verified: data.businessStatus === "SUCCESSFUL",
        avatar: "/assets/geniecover.jpg", // Default image
        coverImage: "/assets/bg3.jpg", // Default image
        description: `${data.businessName} is a ${data.subscriptionType.toLowerCase()} subscription business.`,
        services: data.services.map(service => `${service.category}: ${service.serviceName}`),
        contact: {
          email: data.email,
          phone: data.businessPhoneNumber || data.phoneNumber,
          website: "geniemarketplaces.com", // Default website
          address: "Ghana" // Default address
        },
        availability: {
          hours: "Monday - Friday: 9:00 AM - 5:00 PM", // Default hours
          responseTime: "Usually responds within 2 hours" // Default response time
        },
        subscription: {
          type: data.subscriptionType,
          status: data.subscriptionStatus,
          startDate: data.subscriptionStartDate,
          endDate: data.subscriptionEndDate,
          totalAmount: data.totalAmount
        }
      };

      setVendorData(transformedData);
      setIsLoading(false);
    } catch (err) {
      console.error("Error fetching profile data:", err);
      setError(err.message);
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested objects
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleServiceChange = (index, value) => {
    const updatedServices = [...formData.services];
    updatedServices[index] = value;
    setFormData({
      ...formData,
      services: updatedServices
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setVendorData({...formData});
    setIsEditing(false);
    // In a real application, you would send this data to your backend API
    alert("Profile updated successfully!");
  };

  // Simple star rating component
  const StarRating = ({ rating }) => {
    return (
      <div className="vp-star-rating">
        {[...Array(5)].map((_, i) => (
          <span key={i} className={`vp-star ${i < Math.floor(rating) ? 'vp-star-filled' : ''}`}>★</span>
        ))}
      </div>
    );
  };

  // Loading component
  const LoadingProfile = () => (
    <div className="vp-loading">
      <p>Loading profile data...</p>
    </div>
  );

  // Error component
  const ErrorMessage = () => (
    <div className="vp-error">
      <h3>Error Loading Profile</h3>
      <p>{error}</p>
      <button onClick={fetchProfileData} className="vp-btn-primary">Retry</button>
    </div>
  );

  // Subscription Info Component
  const SubscriptionInfo = () => (
    <div className="vp-content-block">
      <h3 className="vp-heading">Subscription Information</h3>
      <div className="vp-subscription-info">
        <div className="vp-subscription-item">
          <span className="vp-icon">🏆</span>
          <div className="vp-subscription-details">
            <p className="vp-label">Subscription Type</p>
            <p className="vp-text vp-subscription-type">{vendorData.subscription.type}</p>
          </div>
        </div>
        <div className="vp-subscription-item">
          <span className="vp-icon">📊</span>
          <div className="vp-subscription-details">
            <p className="vp-label">Status</p>
            <p className="vp-text vp-subscription-status">{vendorData.subscription.status}</p>
          </div>
        </div>
        <div className="vp-subscription-item">
          <span className="vp-icon">📅</span>
          <div className="vp-subscription-details">
            <p className="vp-label">Period</p>
            <p className="vp-text">{vendorData.subscription.startDate} to {vendorData.subscription.endDate}</p>
          </div>
        </div>
        <div className="vp-subscription-item">
          <span className="vp-icon">💰</span>
          <div className="vp-subscription-details">
            <p className="vp-label">Amount</p>
            <p className="vp-text">GHS {vendorData.subscription.totalAmount.toFixed(2)}</p>
          </div>
        </div>
      </div>
    </div>
  );

  // Tab Content Components
  const AboutTab = () => (
    <div className="vp-tab-content-section">
      {isEditing ? (
        <form onSubmit={handleSubmit} className="vp-edit-form">
          <div className="vp-form-group">
            <label>Business Name</label>
            <input 
              type="text" 
              name="name" 
              value={formData.name} 
              onChange={handleInputChange}
              className="vp-form-input"
            />
          </div>
          
          <div className="vp-form-group">
            <label>Tagline</label>
            <input 
              type="text" 
              name="tagline" 
              value={formData.tagline} 
              onChange={handleInputChange}
              className="vp-form-input"
            />
          </div>
          
          <div className="vp-form-group">
            <label>Description</label>
            <textarea 
              name="description" 
              value={formData.description} 
              onChange={handleInputChange}
              className="vp-form-textarea"
              rows="4"
            />
          </div>
          
          <div className="vp-form-group">
            <label>Services</label>
            {formData.services.map((service, index) => (
              <input 
                key={index}
                type="text" 
                value={service} 
                onChange={(e) => handleServiceChange(index, e.target.value)}
                className="vp-form-input vp-service-input"
              />
            ))}
          </div>
          
          <div className="vp-form-section">
            <h3>Availability</h3>
            <div className="vp-form-group">
              <label>Business Hours</label>
              <input 
                type="text" 
                name="availability.hours" 
                value={formData.availability.hours} 
                onChange={handleInputChange}
                className="vp-form-input"
              />
            </div>
            
            <div className="vp-form-group">
              <label>Response Time</label>
              <input 
                type="text" 
                name="availability.responseTime" 
                value={formData.availability.responseTime} 
                onChange={handleInputChange}
                className="vp-form-input"
              />
            </div>
          </div>
          
          <div className="vp-form-actions">
            <button type="submit" className="vp-btn-primary">Save Changes</button>
            <button 
              type="button" 
              className="vp-btn-secondary" 
              onClick={() => setIsEditing(false)}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <>
          <div className="vp-content-block">
            <h3 className="vp-heading">About Us</h3>
            <p className="vp-text">{vendorData.description}</p>
          </div>
          
          <div className="vp-content-block">
            <h3 className="vp-heading">Our Services</h3>
            {vendorData.services.length > 0 ? (
              <ul className="vp-services-list">
                {vendorData.services.map((service, index) => (
                  <li key={index} className="vp-service-item">
                    <span className="vp-check-icon">✓</span>
                    <span className="vp-service-text">{service}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="vp-text">No services listed yet.</p>
            )}
          </div>
          
          <SubscriptionInfo />
          
          <div className="vp-content-block">
            <h3 className="vp-heading">Availability</h3>
            <div className="vp-availability-item">
              <span className="vp-icon">📅</span>
              <span className="vp-text">{vendorData.availability.hours}</span>
            </div>
            <div className="vp-availability-item">
              <span className="vp-icon">⏱️</span>
              <span className="vp-text">{vendorData.availability.responseTime}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );

  const ContactTab = () => (
    <div className="vp-tab-content-section">
      {isEditing ? (
        <form onSubmit={handleSubmit} className="vp-edit-form">
          <h3>Contact Information</h3>
          
          <div className="vp-form-group">
            <label>Email</label>
            <input 
              type="email" 
              name="contact.email" 
              value={formData.contact.email} 
              onChange={handleInputChange}
              className="vp-form-input"
            />
          </div>
          
          <div className="vp-form-group">
            <label>Phone</label>
            <input 
              type="text" 
              name="contact.phone" 
              value={formData.contact.phone} 
              onChange={handleInputChange}
              className="vp-form-input"
            />
          </div>
          
          <div className="vp-form-group">
            <label>Website</label>
            <input 
              type="text" 
              name="contact.website" 
              value={formData.contact.website} 
              onChange={handleInputChange}
              className="vp-form-input"
            />
          </div>
          
          <div className="vp-form-group">
            <label>Address</label>
            <input 
              type="text" 
              name="contact.address" 
              value={formData.contact.address} 
              onChange={handleInputChange}
              className="vp-form-input"
            />
          </div>
          
          <div className="vp-form-actions">
            <button type="submit" className="vp-btn-primary">Save Changes</button>
            <button 
              type="button" 
              className="vp-btn-secondary" 
              onClick={() => setIsEditing(false)}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <>
          <h3 className="vp-heading">Contact Information</h3>
          
          <div className="vp-contact-info">
            <div className="vp-contact-item">
              <span className="vp-icon">✉️</span>
              <div className="vp-contact-details">
                <p className="vp-label">Email</p>
                <p className="vp-text">{vendorData.contact.email}</p>
              </div>
            </div>
            
            <div className="vp-contact-item">
              <span className="vp-icon">📞</span>
              <div className="vp-contact-details">
                <p className="vp-label">Phone</p>
                <p className="vp-text">{vendorData.contact.phone}</p>
              </div>
            </div>
            
            <div className="vp-contact-item">
              <span className="vp-icon">🌐</span>
              <div className="vp-contact-details">
                <p className="vp-label">Website</p>
                <p className="vp-text">{vendorData.contact.website}</p>
              </div>
            </div>
            
            <div className="vp-contact-item">
              <span className="vp-icon">📍</span>
              <div className="vp-contact-details">
                <p className="vp-label">Address</p>
                <p className="vp-text">{vendorData.contact.address}</p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );

  const renderTabContent = () => {
    switch(activeTab) {
      case 'about':
        return <AboutTab />;
      case 'contact':
        return <ContactTab />;
      default:
        return <AboutTab />;
    }
  };

  // If loading or error, show appropriate message
  if (isLoading) return <LoadingProfile />;
  if (error) return <ErrorMessage />;

  return (
    <div className="vendor-profile-container">
      {/* Cover Image with Edit Option */}
      <div className="vp-cover-image">
        <img 
          src={vendorData.coverImage} 
          alt="Cover" 
          className="vp-cover-img"
        />
        {!isEditing && (
          <button 
            className="vp-edit-profile-btn"
            onClick={() => setIsEditing(true)}
          >
            <i className="fas fa-edit"></i> Edit Profile
          </button>
        )}
      </div>
      
      {/* Profile Header */}
      <div className="vp-profile-header-container">
        <div className="vp-profile-header">
          <div className="vp-profile-header-content">
            {/* Avatar */}
            <div className="vp-avatar-container">
              <img 
                src={vendorData.avatar} 
                alt={vendorData.name} 
                className="vp-avatar" 
              />
              {vendorData.verified && (
                <div className="vp-verified-badge">✓</div>
              )}
            </div>
            
            {/* Profile Info */}
            <div className="vp-profile-info">
              <div className="vp-profile-info-main">
                <div className="vp-profile-details">
                  <h1 className="vp-vendor-name">{vendorData.name}</h1>
                  <p className="vp-vendor-tagline">{vendorData.tagline}</p>
                  
                  <div className="vp-rating-container">
                    <StarRating rating={vendorData.rating} />
                    <span className="vp-rating-value">{vendorData.rating}</span>
                    <span className="vp-separator">•</span>
                    <span className="vp-review-count">{vendorData.reviewCount} reviews</span>
                  </div>
                </div>
                
                <div className="vp-contact-quick-links">
                  <div className="vp-contact-quick-item">
                    <span className="vp-icon">✉️</span>
                    <span className="vp-text">{vendorData.contact.email}</span>
                  </div>
                  <div className="vp-contact-quick-item">
                    <span className="vp-icon">📞</span>
                    <span className="vp-text">{vendorData.contact.phone}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Tabs Navigation */}
      <div className="vp-tabs-container">
        <div className="vp-tabs-nav">
          <button 
            className={`vp-tab-button ${activeTab === 'about' ? 'vp-tab-active' : ''}`}
            onClick={() => setActiveTab('about')}
          >
            About
          </button>
          <button 
            className={`vp-tab-button ${activeTab === 'contact' ? 'vp-tab-active' : ''}`}
            onClick={() => setActiveTab('contact')}
          >
            Contact
          </button>
        </div>
      </div>
      
      {/* Tab Content */}
      <div className="vp-tab-content-container">
        <div className="vp-tab-content">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
}

export default VendorProfile;